// Import column type
import { ColumnDefinitions } from '../state-management/calculation-state';
import { ColumnMap } from '../types';

export interface ColumnFormat {
    id: string;
    name: string;
    label?: string;
    width: number;
    index: number;
    selectable: boolean;
    focusable: boolean;
    hidden?: boolean;
}

export interface ColumnDefinition { [columnName: string]: ColumnFormat }

const nonSelectable = (f: ColumnFormat): ColumnFormat => {
    return {...f, selectable: false};
}

const nonFocusable = (f: ColumnFormat): ColumnFormat => {
    return {...f, focusable: false};
}

const mapToInactiveFormat = (f: ColumnFormat): ColumnFormat => {
    return nonFocusable(nonSelectable(f));
}

export const mapToInactiveDefinition = (f: ColumnDefinition, exceptKeys?: string[]): ColumnDefinition => {
    return Object.keys(f).reduce<ColumnDefinition>((def, key) => {
        def[key] = !!exceptKeys?.length && exceptKeys?.includes(key) ? f[key] : mapToInactiveFormat(f[key]);
        return def;
    }, {})
}

export const CALCULATION_DEFAULT_COLUMNS: ColumnDefinition = {
    // General columns
    'code': { id: 'code', name: 'Code', width: 0.03, index: 0, selectable: true, focusable: true },
    'description': { id: 'description', name: 'Omschrijving', width: 0.19, index: 1, selectable: true, focusable: true },
    'quantity': { id: 'quantity', name: '#', width: 0.03, index: 2, selectable: true, focusable: true },
    'unit': { id: 'unit', name: 'Ehd', width: 0.03, index: 3, selectable: true, focusable: true },

    // Hours
    'hour-norm': { id: 'hour-norm', name: 'Norm uren', label: 'Norm', width: 0.025, index: 4, selectable: true, focusable: true },
    'hour-net': { id: 'hour-net', name: 'Uren', width: 0.025, index: 5, selectable: false, focusable: false },
    'wage-net': { id: 'wage-net', name: 'Netto uren', label: 'Netto', width: 0, index: 6, selectable: false, focusable: false, hidden: true },
    'hour-percent': { id: 'hour-percent', name: '+A%', width: 0.025, index: 7, selectable: true, focusable: true, hidden: false },
    'hour-gross': { id: 'hour-gross', name: 'Verk. A', width: 0.045, index: 8, selectable: false, focusable: false, hidden: false },

    // Materials
    'material-norm': { id: 'material-norm', name: 'MA', width: 0.04, index: 9, selectable: true, focusable: true },
    'material-net': { id: 'material-net', name: 'Netto MA', label: 'Netto', width: 0.045, index: 10, selectable: false, focusable: false },
    'material-percent': { id: 'material-percent', name: '+MA%', width: 0.025, index: 11, selectable: true, focusable: true, hidden: false },
    'material-gross': { id: 'material-gross', name: 'Verk. MA', width: 0.045, index: 12, selectable: false, focusable: false, hidden: false },

    // Equipment
    'equipment-norm': { id: 'equipment-norm', name: 'Materieel', width: 0.04, index: 13, selectable: true, focusable: true },
    'equipment-net': { id: 'equipment-net', name: 'Netto ME', label: 'Netto', width: 0.04, index: 14, selectable: false, focusable: false },
    'equipment-percent': { id: 'equipment-percent', name: '+ME%', width: 0.025, index: 15, selectable: true, focusable: true, hidden: false },
    'equipment-gross': { id: 'equipment-gross', name: 'Verk. ME', width: 0.045, index: 16, selectable: false, focusable: false, hidden: false },

    // Subcontracting
    'subcontracting-norm': { id: 'subcontracting-norm', name: 'OA', width: 0.045, index: 17, selectable: true, focusable: true },
    'subcontracting-net': { id: 'subcontracting-net', name: 'Netto OA', label: 'Netto', width: 0.045, index: 18, selectable: false, focusable: false },
    'subcontracting-percent': { id: 'subcontracting-percent', name: '+OA%', width: 0.025, index: 19, selectable: true, focusable: true, hidden: false },
    'subcontracting-gross': { id: 'subcontracting-gross', name: 'Verk. OA', width: 0.05, index: 20, selectable: false, focusable: false, hidden: false },

    // Totals
    'total-norm': { id: 'total-norm', name: 'Eenheid prijs', label: 'Ehd prijs', width: 0.0425, index: 21, selectable: false, focusable: false },
    'total-net': { id: 'total-net', name: 'Totaal', width: 0.0425, index: 22, selectable: false, focusable: false },
    'total-gross': { id: 'total-gross', name: 'Bruto totaal', width: 0.045, index: 23, selectable: false, focusable: false }
};

export const CALCULATION_CONDENSED_COLUMNS: ColumnDefinition = {
    // General columns
    'code': { id: 'code', name: 'Code', width: 0.03, index: 0, selectable: true, focusable: true },
    'description': { id: 'description', name: 'Omschrijving', width: 0.43, index: 1, selectable: true, focusable: true },
    'quantity': { id: 'quantity', name: 'Hoeveelheid', width: 0.03, index: 2, selectable: true, focusable: true },
    'unit': { id: 'unit', name: 'Eenheid', width: 0.03, index: 3, selectable: true, focusable: true },

    // Hours
    'hour-norm': { id: 'hour-norm', name: 'Norm uren', label: 'Norm', width: 0.025, index: 4, selectable: true, focusable: true },
    'hour-net': { id: 'hour-net', name: 'Uren', width: 0.025, index: 5, selectable: false, focusable: false },
    'wage-net': { id: 'wage-net', name: 'Netto uren', label: 'Netto', width: 0.045, index: 6, selectable: false, focusable: false, hidden: false },
    'hour-percent': { id: 'hour-percent', name: '% Arbeid', width: 0.025, index: 7, selectable: true, focusable: true, hidden: true },
    'hour-gross': { id: 'hour-gross', name: 'Verkoop arbeid', width: 0.045, index: 8, selectable: false, focusable: false, hidden: true },

    // Materials
    'material-norm': { id: 'material-norm', name: 'Materiaal', width: 0.04, index: 9, selectable: true, focusable: true },
    'material-net': { id: 'material-net', name: 'Netto materiaal', label: 'netto', width: 0.045, index: 10, selectable: false, focusable: false },
    'material-percent': { id: 'material-percent', name: '% Materiaal', width: 0.025, index: 11, selectable: true, focusable: true, hidden: true },
    'material-gross': { id: 'material-gross', name: 'Verkoop materiaal', width: 0.045, index: 12, selectable: false, focusable: false, hidden: true },

    // Equipment
    'equipment-norm': { id: 'equipment-norm', name: 'Materieel', width: 0.04, index: 13, selectable: true, focusable: true },
    'equipment-net': { id: 'equipment-net', name: 'Netto materieel', label: 'Netto', width: 0.04, index: 14, selectable: false, focusable: false },
    'equipment-percent': { id: 'equipment-percent', name: '% Materieel', width: 0.04, index: 15, selectable: true, focusable: true, hidden: true },
    'equipment-gross': { id: 'equipment-gross', name: 'Verkoop materieel', width: 0.045, index: 16, selectable: false, focusable: false, hidden: true },

    // Subcontracting
    'subcontracting-norm': { id: 'subcontracting-norm', name: 'Onderaaneming', width: 0.045, index: 17, selectable: true, focusable: true },
    'subcontracting-net': { id: 'subcontracting-net', name: 'Netto onderaaneming', label: 'Netto', width: 0.045, index: 18, selectable: false, focusable: false },
    'subcontracting-percent': { id: 'subcontracting-percent', name: '% O.A.', width: 0.03, index: 19, selectable: true, focusable: true, hidden: true },
    'subcontracting-gross': { id: 'subcontracting-gross', name: 'Verkoop onderaaneming', width: 0.05, index: 20, selectable: false, focusable: false, hidden: true },

    // Totals
    'total-norm': { id: 'total-norm', name: 'Eenheid prijs', label: 'Ehd prijs', width: 0.0425, index: 21, selectable: false, focusable: false },
    'total-net': { id: 'total-net', name: 'Totaal', width: 0.0425, index: 22, selectable: false, focusable: false },
    'total-gross': { id: 'total-gross', name: 'Bruto totaal', width: 0.045, index: 23, selectable: false, focusable: false }
};

export const COLUMN_CONFIGS: { [id: string]: ColumnMap } = {
    'default': CALCULATION_DEFAULT_COLUMNS,
    'condensed': CALCULATION_CONDENSED_COLUMNS
}

export const TAIL_COLUMNS: ColumnMap = {
    // General columns
    'spacer': { id: 'spacer', name: 'Spacer', width: .705, index: 0, selectable: false, focusable: false, hidden: false  },
    'type': { id: 'type', name: 'Type', width: .03, index: 1, selectable: true, focusable: true, hidden: false  },
    'description': { id: 'description', name: 'Omschrijving', width: 0.1775, index: 2, selectable: true, focusable: true, hidden: false  },
    'amount': { id: 'amount', name: 'Hoeveelheid', width: 0.0425, index: 3, selectable: true, focusable: true, hidden: false  },
    'total': { id: 'total', name: 'Totaal', width: 0.045, index: 4, selectable: false, focusable: false, hidden: false }
};

export const DEFAULT_COLUMN_DEFINITION: ColumnDefinitions = {
    calculation: CALCULATION_DEFAULT_COLUMNS,
    subcategory: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
    category: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
    subtotal: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
    text: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS, ['description', 'quantity', 'unit']),
    total: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
    readOnly: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
    tailAmount: TAIL_COLUMNS,
    tailPercentage: TAIL_COLUMNS,
    tailText: TAIL_COLUMNS,
    tailPrelimanary: mapToInactiveDefinition(TAIL_COLUMNS),
    tailSubtotal: mapToInactiveDefinition(TAIL_COLUMNS),
} 

export const CONDENSED_COLUMN_DEFINITION: ColumnDefinitions = {
    calculation: CALCULATION_CONDENSED_COLUMNS,
    subcategory: mapToInactiveDefinition(CALCULATION_CONDENSED_COLUMNS),
    category: mapToInactiveDefinition(CALCULATION_CONDENSED_COLUMNS),
    subtotal: mapToInactiveDefinition(CALCULATION_CONDENSED_COLUMNS),
    text: mapToInactiveDefinition(CALCULATION_CONDENSED_COLUMNS, ['description', 'quantity', 'unit']),
    total: mapToInactiveDefinition(CALCULATION_CONDENSED_COLUMNS),
    readOnly: mapToInactiveDefinition(CALCULATION_CONDENSED_COLUMNS),
    tailAmount: TAIL_COLUMNS,
    tailPercentage: TAIL_COLUMNS,
    tailText: TAIL_COLUMNS,
    tailPrelimanary: mapToInactiveDefinition(TAIL_COLUMNS),
    tailSubtotal: mapToInactiveDefinition(TAIL_COLUMNS),
} 
